import { useMutation, useQueryClient, type MutationMeta } from '@tanstack/react-query';
import { IssueCommentType, type IIssue } from '../../../trusstor/index.js';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { beginIssuePatch, patchCachedIssue, rollbackIssue } from './issueCache.js';

/** `username`: the signed-in user's — the optimistic comment's sender until the server's copy lands.
 *  `meta`: `OWNS_ERROR_FEEDBACK` when the caller reports a failure itself. */
export function useAddTextComment(
  siteId: string | null,
  issueId: string,
  username: string | undefined,
  meta?: MutationMeta,
) {
  const { addTextCommentApi } = useClientApis().issues;
  const qc = useQueryClient();
  const sender = username ?? '?';

  return useMutation({
    meta,
    mutationFn: (value: string) => {
      if (!siteId) throw new Error('addTextComment called without siteId');
      return addTextCommentApi(siteId, issueId, value);
    },
    // The comment shows at once, as the sender's, until the server's issue lands.
    onMutate: async (value) => ({
      previous: await beginIssuePatch(qc, siteId, issueId, (i) => {
        const temp = {
          commentId: `temp-${Math.random().toString(36).slice(2)}`,
          dateCreated: new Date(),
          sender: { name: sender, username: sender },
          content: { type: IssueCommentType.TEXT, value },
        };
        return { ...i, comments: [...(i.comments ?? []), temp] } as IIssue;
      }),
    }),
    onError: (_err, _value, ctx) => rollbackIssue(qc, siteId, ctx?.previous, issueId),
    onSuccess: (updated) => patchCachedIssue(qc, siteId, issueId, () => updated),
  });
}
